<script setup lang="ts">
/**
 * Loads a deck for DeckPlayerShell and shows loading/error states. Bootstrap mapping,
 * retry and reset live in useDeckPlayerHost.
 */
import { ref } from 'vue'
import type { FlowContext, FlowResult } from '../../../../../shared/player-contracts/src'
import type { FlowGraphV1 } from '../flowchart/flowGraph'
import type { MediaMaterial } from '../flowchart/runtime/media'
import { useDeckPlayerHost } from '../host/useDeckPlayerHost'
import type { DeckPlayerModel } from '../types'
import DeckPlayerShell from './DeckPlayerShell.vue'
import DeckPlayerError from './host/DeckPlayerError.vue'
import DeckPlayerLoading from './host/DeckPlayerLoading.vue'

const props = defineProps<{
  deckId: string
  model: DeckPlayerModel
  passthroughProps?: Record<string, unknown>
  restRoot?: string
  nonce?: string
  /** When set, overrides the flowGraph from the backend (e.g. for live editor preview). */
  overrideFlowGraph?: FlowGraphV1 | null
  /** When true, graph execution pauses at every node waiting for stepSignal to increment. */
  manualStep?: boolean
  /** Increment this to advance one step in manual step mode. */
  stepSignal?: number
  /** Debug-only: milliseconds to pause between auto-advanced nodes (0 = instant). */
  debugStepDelay?: number
  /** When true, suppress the floating debug panel and flow canvas (e.g. inside the flowchart editor). */
  inEditor?: boolean
  /** Called when the flow reaches a phase node and the interlude screen is dismissed. */
  onPhaseChange?: (phaseId: string) => void
  /** Called when the flow reaches the end node, with the run's answers and signals. */
  onComplete?: (result: FlowResult) => void
  /** Course data for phase getters (stored answers of the course's other levels). */
  flowContext?: FlowContext | null
  /** Called once after the deck data has loaded (or failed) and the initial render is settled. */
  onLoad?: () => void
  /** Called when the flow hits a show-media node in panel mode (fire-and-forget). */
  onShowMedia?: (info: {
    mediaType: 'image' | 'video' | 'audio' | 'pdf'
    mediaName: string
    mediaId: string
  }) => void
  /** Media materials from the course — resolved URLs for show-media nodes. */
  mediaMaterials?: MediaMaterial[]
  /** Called when standalone/split media pauses the flow; receives a `resume()` callback. */
  onMediaPause?: (
    info: { displayMode: string; mediaType: string; mediaName: string; mediaId: string },
    resume: () => void,
  ) => void
  /** Called when the deck enters the pre-start gate; receives a `start()` function to trigger flow start. */
  onAwaitingStart?: (start: () => void) => void
  /** If true, emit completion callbacks but do not render the built-in completion UI. */
  suppressCompletionUi?: boolean
  /** Editor preview helper: keep the last pulled card visible after flow end. */
  keepCardOnFlowEnd?: boolean
}>()

const emit = defineEmits<{
  (
    e: 'flowchart-step-changed',
    payload: { nodeId: string | null; needsUserInteraction?: boolean },
  ): void
}>()

const shellRef = ref<InstanceType<typeof DeckPlayerShell> | null>(null)
defineExpose({
  stepForward: () => shellRef.value?.stepForward(),
  shell: shellRef,
})

/** Hands the host page a start() for the pre-start gate. */
const handleAwaitingStart = () => {
  props.onAwaitingStart?.(() => shellRef.value?.startGraph?.())
}

const host = useDeckPlayerHost(props)
const {
  loading,
  error,
  bootstrap,
  diagnostics,
  debugEnabled,
  playerProps,
  deckTemplate,
  flowPropagationBlockedUntil,
  cards,
  isLoggedIn,
  suppressProgressWrites,
  initialValues: effectiveInitialValues,
  initialFlowPhaseId,
  forceComplete,
  allowReplayWhenCompleted,
  retryAllowed,
  retryCooldown,
  shellKey,
  handleRetry,
  handleCompleted,
  handleFlowEnd,
  handlePhaseInterlude,
  handleShowMedia,
  handleFlowContinue,
  handleFlowReset,
} = host
</script>

<template>
  <div class="host">
    <DeckPlayerLoading v-if="loading" />
    <DeckPlayerError
      v-else-if="error"
      :error="error"
      :diagnostics="diagnostics"
      :debug-enabled="debugEnabled"
    />

    <DeckPlayerShell
      v-else
      ref="shellRef"
      :key="shellKey"
      :deck-id="props.deckId"
      :deck-title="playerProps.titleOverride || bootstrap?.deck?.title"
      :cards="cards"
      :model="props.model"
      :deck-card-ids="bootstrap?.deck?.cardIds"
      :deck-groups="bootstrap?.deck?.groups"
      :flow-graph="
        props.overrideFlowGraph !== undefined
          ? props.overrideFlowGraph
          : (bootstrap?.deck?.flowGraph ?? null)
      "
      :manual-step="props.manualStep"
      :step-signal="props.stepSignal"
      :debug-step-delay="props.debugStepDelay"
      :in-editor="props.inEditor"
      :is-logged-in="isLoggedIn"
      :debug="debugEnabled"
      :rest-root="props.restRoot"
      :nonce="props.nonce"
      :initial-values="effectiveInitialValues"
      :initial-flow-phase-id="initialFlowPhaseId"
      :player-props="playerProps"
      :deck-template="deckTemplate"
      :suppress-progress-writes="suppressProgressWrites"
      :force-complete="forceComplete"
      :allow-replay-when-completed="allowReplayWhenCompleted"
      :retry-allowed="retryAllowed"
      :suppress-completion-ui="props.suppressCompletionUi === true"
      :keep-card-on-flow-end="props.keepCardOnFlowEnd === true"
      :retry-blocked-until="retryAllowed ? null : retryCooldown.nextAllowedAt"
      :flow-propagation-blocked-until="flowPropagationBlockedUntil"
      :media-materials="props.mediaMaterials"
      :flow-context="props.flowContext"
      :on-media-pause="props.onMediaPause"
      @retry="handleRetry"
      @completed="handleCompleted"
      @flowchart-step-changed="(p) => emit('flowchart-step-changed', p)"
      @flow-end="handleFlowEnd"
      @phase-interlude="handlePhaseInterlude"
      @show-media="handleShowMedia"
      @flow-continue="handleFlowContinue"
      @flow-reset="handleFlowReset"
      @awaiting-start="handleAwaitingStart"
    />
  </div>
</template>

<style scoped>
.host {
  width: 100%;
  height: 100%;
}
</style>
